import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

const StickyDialog = ({ position, content, onClose }) => {
  const dialogRef = useRef(null);
  
  // 点击外部关闭对话框
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);
  
  // ESC键关闭对话框
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // 定时关闭对话框。就算没有这里的定时关闭dlg，其实在App.js从后端刷新revision那边也会导致关闭，FIXME：不过还没有深入这个分析代码（AI不靠谱），担心关闭和资源回收不牢靠，所以这里还是加了个定时关闭。以后要修正这方面的功能，记得App.js从后端刷新revision也是会有影响的地方。
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 10000); // 10秒后自动关闭。不过app.js里定时5秒获数据会导致刷屏导致关闭

    return () => clearTimeout(timer); // 清理定时器
  }, [onClose]);

  // 动态定位 - 使用absolute而不是fixed以便跟随滚动
  const style = {
    position: 'absolute', // 改为absolute以便跟随文档滚动
    top: 0, // 初始位置，会在updateDialogPosition中更新
    left: 0, // 初始位置，会在updateDialogPosition中更新
    zIndex: 1000,
    backgroundColor: '#ffff88', // Yellow sticky note color
    fontFamily: '"Reenie Beanie", cursive', // Handwritten font
    transform: 'rotate(-2deg)', // Slight rotation for a natural look
    boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.3)', // Shadow for depth
    borderRadius: '8px', // Rounded corners
    padding: '12px', // Slightly more padding
    maxWidth: '300px',
    maxHeight: '200px',
    overflow: 'auto',
    border: '1px solid #e0e0e0', // Light border
  };

  const updateDialogPosition = () => {
    if (dialogRef.current) {
      // 获取当前滚动位置
      const scrollX = window.scrollX || window.pageXOffset;
      const scrollY = window.scrollY || window.pageYOffset;
      
      // 获取原始元素的位置（相对于视口）
      const relativeX = position.x;
      const relativeY = position.y;
      
      // 计算绝对位置（相对于文档）
      const absoluteX = relativeX + scrollX;
      const absoluteY = relativeY + scrollY;
      
      const dialogRect = dialogRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      let newTop = absoluteY;
      let newLeft = absoluteX;

      // 防止对话框超出视口底部
      if (relativeY + dialogRect.height > viewportHeight) {
        newTop = scrollY + viewportHeight - dialogRect.height - 10;
      }

      // 防止对话框超出视口右侧
      if (relativeX + dialogRect.width > viewportWidth) {
        newLeft = scrollX + viewportWidth - dialogRect.width - 10;
      }

      // 确保对话框不会挡住 RevisionCard
      const revisionCard = document.querySelector('.revision-card');
      if (revisionCard) {
        const revisionCardRect = revisionCard.getBoundingClientRect();
        if (newTop < revisionCardRect.bottom && newLeft < revisionCardRect.right) {
          newTop = revisionCardRect.bottom + 10;
        }
      }

      // 设置对话框位置
      dialogRef.current.style.top = `${newTop}px`;
      dialogRef.current.style.left = `${newLeft}px`;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      updateDialogPosition();
    };

    // 初始化位置
    updateDialogPosition();
    
    // 添加滚动事件监听
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('scroll', handleScroll, { passive: true });
    
    // 清理函数
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [position]);
  
  // 确保位置在position变化时更新
  useEffect(() => {
    updateDialogPosition();
  }, [position]);

  useEffect(() => {
    const handleResize = () => {
      updateDialogPosition();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  useEffect(() => {
    if (dialogRef.current) {
      const textarea = dialogRef.current.querySelector('textarea');
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  }, [content]);

  return createPortal(
    <div ref={dialogRef} style={style}>
      <textarea 
        readOnly 
        value={content} 
        style={{ 
          height: 'auto', 
          minHeight: '50px', 
          maxHeight: '200px', 
          resize: 'none',
          border: 'none', // Remove the border
          backgroundColor: 'transparent', // Match the sticky note background
          color: 'black', // Ensure text color is visible
          fontFamily: '"Reenie Beanie", cursive', // Match the sticky note font
          fontSize: '12px', // Adjust font size as needed
          lineHeight: '1.5', // Adjust line height for better readability
          outline: 'none', // Remove focus outline
        }} 
      />
    </div>,
    document.body
  );
};

StickyDialog.propTypes = {
  position: PropTypes.shape({
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
  }).isRequired,
  content: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default StickyDialog;